import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import GenericBasicInfo from "../../../../../components/cards/GenericBasicInfo";
import {
  cardStyles,
  modalGenericStyles,
  variables,
} from "../../../../../variables";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  retrieveCoachReservation,
  patchCoachReservation,
} from "../../../../../api/CRS/Coaches";
import { Price } from "../../../../../components/Price";
import { DateTime } from "luxon";
import GuestCard from "../../../../../components/cards/GuestInfo";
import useReservationDetails from "../../../../../hooks";
import EditGuestModal from "../../../CommonCRSModals/EditGuestModal";
import EmailsCard from "../../../../../components/cards/emails";
import Loader from "../../../../../components/Loader";
import EditCRSTagModal from "../../../CommonCRSModals/EditCRSTagModal";
import { toast } from "react-toastify";
import ReservationEventsModal from "../../../ReservationEventsModal";
import { DocumentsCRSCard } from "../../../CommonCRSModals/DocumentsCRS/List";
import ReservationsStatusTag from "../../../../../components/Tags/ReservationsStatusTag";
import TabStrip from "../../../../../components/TabStrip";
import { CustomButton } from "../../../../../components/CustomButton";
import COALogsTab from "./COALogsTab";
import { genericRequestErrorHandler } from "../../../../../api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Receivables from "../../../CommonCRSModals/Receivables";
import { InternalLogs } from "../../../CommonCRSModals/InternalLogs";

const styles = createUseStyles({
  ...modalGenericStyles,
  DetailCoachModal: {
    ...modalGenericStyles.modal,
  },
  modalCard: {
    ...modalGenericStyles.card,
    gridTemplateRows: "max-content max-content 1fr max-content",
  },
  modalCardHeader: {
    ...modalGenericStyles.cardHeader,
  },
  modalCardBody: {
    ...modalGenericStyles.cardBody,
    display: "grid",
    padding: "",
    overflowX: "hidden",
  },
  modalCardActions: {
    ...modalGenericStyles.cardActions,
  },
  card: { ...cardStyles.card, maxWidth: "100%" },
  cardHeader: cardStyles.header,
  cardBody: {
    ...cardStyles.body,
    gap: variables.normal_gap,
    maxWidth: "100%",
    overflow: "auto",
    justifyContent: "start",
  },
  firstRow: {
    display: "grid",
    gridTemplateColumns: "repeat(1, 1fr)",
    padding: variables.normal_gap,
    gap: variables.normal_gap,
  },
  creatorCardBody: {
    extend: "cardBody",
    padding: [`calc(${variables.normal_gap} * 3.3)`, variables.normal_gap],
  },
  componentRow: {
    extend: "firstRow",
    gridTemplateColumns: "repeat(1, 1fr)",
  },
  [`@media ${variables.media.smallscreen}`]: {
    modalCardBody: {
      minWidth: "60rem",
    },
    firstRow: {
      gridTemplateColumns: "1fr 1fr",
      justifyItems: "center",
    },
  },
});
const DetailCoachModal = ({ reference, onClose }) => {
  const classes = styles();

  const [activeTab, setActiveTab] = useState("basicInfo");

  const queryClient = useQueryClient();

  const menuTabs = {
    basicInfo: "Basic Info",
    reservationEvents: "Reservation Events",
    receivables: "Receivables",
    documents: "Documents",
    internalLogs: "Internal Logs",
    logs: "User Action Logs",
  };

  const {
    data: coach,
    guestEditMode,
    guestEditId,
    editCRSTagMode,
    reservationEventsModal,
    openGuestModal,
    closeGuestModal,
    openEditCRSTagModal,
    closeEditCRSTagModal,
    closeReservationEventsModal,
    // loading,
  } = useReservationDetails({
    reference,
    retrieveReservationData: retrieveCoachReservation,
    onCloseDetails: onClose,
  });

  const basicDataCpl = [
    ["Reference", "reference"],
    ["Tags", null, _.get(coach, "tags", []).join(",")],
    ["Company Name", "company_name"],
    ["Adults", "adults"],
    ["Children", null, _.get(_.get(coach, "data.children")) || "0"],
    [
      "Departure",
      null,
      DateTime.fromISO(_.get(coach, "data.departure")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    [
      "Arrival",
      null,
      DateTime.fromISO(_.get(coach, "data.arrival")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    ["Destination", null, _.get(coach, "data.destination.country_code")],
    ["Drop Off", "dropoff"],
    [
      "Original Service Price",
      null,
      <Price
        currency={_.get(coach, "data.currency")}
        price={_.get(coach, "data.original_service_price")}
      />,
    ],
    ["Parking Fees", "parking_fees"],
    ["Market Type", "market_type"],
    ["Source Entity", "source_entity"],
    [
      "Created",
      null,
      DateTime.fromISO(_.get(coach, "data.created")).toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      ),
    ],
    [
      "Status",
      null,
      <ReservationsStatusTag status={_.get(coach, "data.status")} />,
    ],
  ];
  const creatorDataCpl = [
    ["External Creator", "external_creator"],
    [
      "Creator",
      null,
      _.get(coach, "data.guests", []).map((des) => _.get(des, "creator")),
    ],
  ];

  const { mutate: onSubmitTags, isLoading: tagsLoading } = useMutation({
    mutationFn: (values) => {
      return patchCoachReservation({ payload: values, reference });
    },
    onSuccess: () => {
      toast.success("Tags updated successfully");
      queryClient.invalidateQueries("coaches");
      onClose();
    },
    onError: (error) => {
      genericRequestErrorHandler(error);
    },
  });

  return (
    <div id="DetailCOAModal" className={classes.DetailCoachModal}>
      <div className={classes.modalCard}>
        <div className={classes.modalCardHeader}>
          <span>Coach Details</span>
        </div>
        <TabStrip
          tabs={Object.entries(menuTabs)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <div className={classes.modalCardBody}>
          {_.isEmpty(coach) ? (
            <Loader />
          ) : (
            <React.Fragment>
              {activeTab === "basicInfo" && (
                <React.Fragment>
                  <div className={classes.firstRow}>
                    <div className={classes.cardBody}>
                      <GenericBasicInfo
                        actions={[["Edit", () => openEditCRSTagModal()]]}
                        header={"Basic Data"}
                        cardInfo={basicDataCpl}
                        objectData={_.get(coach, "data")}
                      />
                    </div>
                    <div className={classes.creatorCardBody}>
                      <GenericBasicInfo
                        header="Creator Data"
                        cardInfo={creatorDataCpl}
                        objectData={_.get(coach, "data")}
                      />
                    </div>
                  </div>
                  <div className={classes.componentRow}>
                    <div className={classes.cardBody}>
                      <GuestCard
                        title={"Guest Informations"}
                        guests={_.get(coach, "data.guests", [])}
                        onCloseEdit={() => closeGuestModal()}
                        onEditGuest={(id) => {
                          openGuestModal(id);
                        }}
                      />
                      <EmailsCard
                        title={"Email Informations"}
                        market_type={_.get(coach, "data.market_type")}
                        reservation_id={_.get(coach, "data.reference")}
                        service_type="COA"
                        status={_.get(coach, "data.status")}
                      />
                    </div>
                  </div>
                </React.Fragment>
              )}
              {activeTab === "reservationEvents" && (
                <ReservationEventsModal
                  header="Reservation Events"
                  onClose={() => closeReservationEventsModal()}
                  events={_.get(coach, "data.reservation_events", [])}
                />
              )}
              {guestEditMode && !reservationEventsModal && !editCRSTagMode && (
                <EditGuestModal
                  onClose={closeGuestModal}
                  guestId={guestEditId}
                  queryKey={`coach_reservation_users_documents`}
                  reference={_.get(coach, "data.reference")}
                  sourceEntity={_.get(coach, "data.source_entity", "")}
                />
              )}
              {!guestEditMode && !reservationEventsModal && editCRSTagMode && (
                <EditCRSTagModal
                  header={"Coach"}
                  tagsLoading={tagsLoading}
                  tags={_.get(coach, "data.tags")}
                  onClose={closeEditCRSTagModal}
                  onSubmitTags={onSubmitTags}
                />
              )}
              {activeTab === "receivables" && (
                <Receivables data={_.get(coach, "data.payment_links", [])} />
              )}
              {activeTab === "documents" && (
                <DocumentsCRSCard
                  title="Documents"
                  queryKey={`coach_reservation_document`}
                  showAddButton={true}
                  tags={[`${_.get(coach, "data.reference")}_reservation_COA`]}
                  reference={_.get(coach, "data.reference")}
                  sourceEntity={_.get(coach, "data.source_entity", "")}
                />
              )}
              {activeTab === "internalLogs" && (
                <InternalLogs
                  prebookData={_.get(coach, "data.prebook_data", {})}
                  bookData={_.get(coach, "data.post_booking_data", {})}
                />
              )}
              {activeTab === "logs" && <COALogsTab />}
            </React.Fragment>
          )}
        </div>
        <div className={classes.modalCardActions}>
          <CustomButton id="cancel" appearance="ghost" onClick={onClose}>
            <strong>Cancel</strong>
          </CustomButton>
        </div>
      </div>
    </div>
  );
};
DetailCoachModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};
export default DetailCoachModal;
